import { useCallback, useEffect, useState } from 'react';
import {
  deleteLibraryEntry,
  exportLibraryBundle,
  getLibraryEntry,
  importLibraryBundle,
  listLibraryEntries,
  loadActiveMeta,
  persistActiveMeta,
  saveLibraryEntry,
  updateLibraryEntryMeta,
} from './scenarioLibrary';

/**
 * @param {'economics'|'forecast'} kind
 * @param {{
 *   getPayload: () => object,
 *   applyPayload: (payload: object) => void,
 * }} options
 */
export function useScenarioLibrary(kind, { getPayload, applyPayload }) {
  const [entries, setEntries] = useState(() => listLibraryEntries(kind));
  const [activeMeta, setActiveMeta] = useState(() => loadActiveMeta(kind));
  const [message, setMessage] = useState(null);

  const refresh = useCallback(() => {
    setEntries(listLibraryEntries(kind));
    setActiveMeta(loadActiveMeta(kind));
  }, [kind]);

  useEffect(() => {
    const onStorage = (event) => {
      if (
        event.key === `ffc.admin.${kind}.library.v1` ||
        event.key === `ffc.admin.${kind}.activeMeta.v1`
      ) {
        refresh();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [kind, refresh]);

  const saveAs = useCallback(
    ({ name, notes, id } = {}) => {
      try {
        const entry = saveLibraryEntry(kind, {
          id,
          name,
          notes,
          payload: getPayload(),
        });
        refresh();
        setMessage(`Saved “${entry.name}”.`);
        return entry;
      } catch (err) {
        setMessage(err.message);
        return null;
      }
    },
    [kind, getPayload, refresh]
  );

  const loadEntry = useCallback(
    (id) => {
      const entry = getLibraryEntry(kind, id);
      if (!entry) {
        setMessage('Entry not found.');
        return null;
      }
      applyPayload(JSON.parse(JSON.stringify(entry.payload)));
      persistActiveMeta(kind, { libraryId: entry.id, libraryName: entry.name });
      refresh();
      setMessage(`Loaded “${entry.name}”.`);
      return entry;
    },
    [kind, applyPayload, refresh]
  );

  const removeEntry = useCallback(
    (id) => {
      deleteLibraryEntry(kind, id);
      refresh();
      setMessage('Deleted.');
    },
    [kind, refresh]
  );

  const renameEntry = useCallback(
    (id, { name, notes }) => {
      try {
        const entry = updateLibraryEntryMeta(kind, id, { name, notes });
        refresh();
        setMessage(`Updated “${entry.name}”.`);
        return entry;
      } catch (err) {
        setMessage(err.message);
        return null;
      }
    },
    [kind, refresh]
  );

  const clearActiveLink = useCallback(() => {
    persistActiveMeta(kind, { libraryId: null, libraryName: null });
    refresh();
  }, [kind, refresh]);

  const downloadLibrary = useCallback(() => {
    const bundle = exportLibraryBundle(kind);
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ffc-${kind}-library-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [kind]);

  const uploadLibrary = useCallback(
    async (file, mode = 'merge') => {
      try {
        const text = await file.text();
        const bundle = JSON.parse(text);
        const result = importLibraryBundle(kind, bundle, mode);
        refresh();
        setMessage(`Imported ${result.count} entries (${result.mode}).`);
        return result;
      } catch (err) {
        setMessage(err.message || 'Import failed.');
        return null;
      }
    },
    [kind, refresh]
  );

  return {
    entries,
    activeMeta,
    message,
    setMessage,
    refresh,
    saveAs,
    loadEntry,
    removeEntry,
    renameEntry,
    clearActiveLink,
    downloadLibrary,
    uploadLibrary,
  };
}
